import { cn } from "@/lib/utils";
import {
  formatTime,
  formatTimeWithTz,
  formatTimeRange,
  formatWindowCompact,
  viewerTzAbbr,
  isLateDeparture,
  isLateArrival,
} from "@/lib/order-time";
import { useViewerTimezone } from "@/hooks/useViewerTimezone";

/**
 * Shared display for an order's Ready (Ship From) and Delivery (Ship To) times.
 * All times are shown in the viewer's own timezone with its abbreviation
 * (docs/TIMEZONES.md), so the stop's location is not needed.
 *
 * Priority chain (single source of truth — keep all surfaces in sync):
 *  - Ready: `actual_departure_time` → "Departed: …" (with scheduled subline),
 *    else the scheduled ready window.
 *  - Delivery: `actual_arrival_time` → "Arrived: …" (with scheduled subline),
 *    else if `status === "in_transit"` and `eapi_eta` → "ETA: …" (with
 *    scheduled subline), else the scheduled delivery window.
 *
 * Two visual variants:
 *  - `compact` — Orders table cells. Tiny two-line block (no "Scheduled:" prefix
 *    when scheduled is the only thing shown).
 *  - `card` — OrderDetailSheet popout + OrderDetail info card. Larger headline
 *    with a "Scheduled: …" subline.
 */

const WARNING_CLASS = "text-[hsl(var(--aurora-status-warning))]";

type OrderLike = {
  status?: string | null;
  ready_time?: string | null;
  ready_time_end?: string | null;
  requested_delivery_time?: string | null;
  requested_delivery_time_end?: string | null;
  ship_from_street_address?: string | null;
  ship_from_city?: string | null;
  ship_from_province_state?: string | null;
  ship_to_street_address?: string | null;
  ship_to_city?: string | null;
  ship_to_province_state?: string | null;
  actual_departure_time?: string | null;
  actual_arrival_time?: string | null;
  eapi_eta?: string | null;
};

type Variant = "compact" | "card";
type CardSize = "sm" | "base";

interface BaseProps {
  order: OrderLike;
  variant?: Variant;
  /** Card variant only. Defaults to "base" (used by OrderDetail page). */
  cardSize?: CardSize;
}

// ---------- Shared rendering ----------

/** Card headline: "MMM d, yyyy at HH:mm[ – HH:mm] TZ". */
function CardTime({
  startUtc,
  endUtc,
  viewerTz,
  prefix,
  headlineClass,
  subClass,
}: {
  startUtc: string | null | undefined;
  endUtc?: string | null;
  viewerTz: string;
  prefix?: string;
  headlineClass: string;
  subClass: string;
}) {
  return (
    <p className={headlineClass}>
      {prefix ? `${prefix}: ` : ""}
      {formatTime(startUtc, viewerTz, "MMM d, yyyy")}{" "}
      <span className={subClass}>
        at {formatTimeRange(startUtc, endUtc, viewerTz, "HH:mm")} {viewerTzAbbr(startUtc, viewerTz)}
      </span>
    </p>
  );
}

function scheduledLine(startUtc: string | null | undefined, endUtc: string | null | undefined, viewerTz: string) {
  return `${formatTime(startUtc, viewerTz, "MMM d, yyyy")} at ${formatTimeRange(startUtc, endUtc, viewerTz, "HH:mm")} ${viewerTzAbbr(startUtc, viewerTz)}`;
}

function cardClasses(cardSize: CardSize, late: boolean) {
  return {
    headlineClass: cn(cardSize === "base" ? "text-base" : "text-sm", "font-semibold", late ? WARNING_CLASS : "text-foreground"),
    subClass: cn(late ? WARNING_CLASS : "text-muted-foreground"),
  };
}

// ---------- Ready (Ship From) ----------

export function ReadyTimeDisplay({ order, variant = "compact", cardSize = "base" }: BaseProps) {
  const viewerTz = useViewerTimezone();
  const late = isLateDeparture(order);
  const actual = order.actual_departure_time ?? null;

  if (variant === "compact") {
    const scheduled = formatWindowCompact(order.ready_time, order.ready_time_end, viewerTz, "MMM d", "HH:mm");
    if (actual) {
      return (
        <div className="text-xs text-muted-foreground">
          <div className={cn(late && WARNING_CLASS)}>Departed: {formatTimeWithTz(actual, viewerTz, "MMM d, HH:mm")}</div>
          <div>Scheduled: {scheduled}</div>
        </div>
      );
    }
    return <div className={cn("text-xs", late ? WARNING_CLASS : "text-muted-foreground")}>{scheduled}</div>;
  }

  const classes = cardClasses(cardSize, late);
  if (actual) {
    return (
      <>
        <CardTime startUtc={actual} viewerTz={viewerTz} prefix="Departed" {...classes} />
        <p className="text-xs text-muted-foreground">Scheduled: {scheduledLine(order.ready_time, order.ready_time_end, viewerTz)}</p>
      </>
    );
  }
  return <CardTime startUtc={order.ready_time} endUtc={order.ready_time_end} viewerTz={viewerTz} {...classes} />;
}

// ---------- Delivery (Ship To) ----------

export function DeliveryTimeDisplay({ order, variant = "compact", cardSize = "base" }: BaseProps) {
  const viewerTz = useViewerTimezone();
  const late = isLateArrival(order);
  const arrived = order.actual_arrival_time ?? null;
  const eta = order.status === "in_transit" ? order.eapi_eta ?? null : null;
  const start = order.requested_delivery_time;
  const end = order.requested_delivery_time_end;

  if (variant === "compact") {
    const scheduled = formatWindowCompact(start, end, viewerTz, "MMM d", "HH:mm");
    const actualish = arrived ? ["Arrived", arrived] : eta ? ["ETA", eta] : null;
    if (actualish) {
      return (
        <div className="text-xs text-muted-foreground">
          <div className={cn(late && WARNING_CLASS)}>
            {actualish[0]}: {formatTimeWithTz(actualish[1], viewerTz, "MMM d, HH:mm")}
          </div>
          <div>Scheduled: {scheduled}</div>
        </div>
      );
    }
    return <div className={cn("text-xs", late ? WARNING_CLASS : "text-muted-foreground")}>{scheduled}</div>;
  }

  const classes = cardClasses(cardSize, late);
  const actualish = arrived ? ["Arrived", arrived] : eta ? ["ETA", eta] : null;
  if (actualish) {
    return (
      <>
        <CardTime startUtc={actualish[1]} viewerTz={viewerTz} prefix={actualish[0]} {...classes} />
        <p className="text-xs text-muted-foreground">Scheduled: {scheduledLine(start, end, viewerTz)}</p>
      </>
    );
  }
  return <CardTime startUtc={start} endUtc={end} viewerTz={viewerTz} {...classes} />;
}
